import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiService } from "../../services/apiService";
import { formatCurrency } from "../../utils/helpers";
import { formatMonthLongYm } from "../../utils/invoiceMonthDisplay";
import { DownloadIcon } from "../icons";
import Toast from "../ui/Toast";

const fieldClass =
  "w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400 focus:border-stone-400 disabled:bg-stone-50";

const formatAsOf = (ym) => {
  if (!ym) return "—";
  return formatMonthLongYm(ym);
};

function getCurrentYearMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthsSinceAsOf(asOfMonth) {
  if (!asOfMonth || !/^\d{4}-\d{2}$/.test(asOfMonth)) return null;
  const currentYm = getCurrentYearMonth();
  const [cy, cm] = currentYm.split("-").map(Number);
  const [ay, am] = asOfMonth.split("-").map(Number);
  return (cy - ay) * 12 + (cm - am);
}

function matchesSearch(entry, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    String(entry.provider || "").toLowerCase().includes(q) ||
    String(entry.billing_account || "").toLowerCase().includes(q) ||
    String(entry.gl_code || "").toLowerCase().includes(q)
  );
}

const AgingBadge = ({ asOfMonth }) => {
  const months = monthsSinceAsOf(asOfMonth);
  if (months == null) {
    return <span className="text-stone-400">—</span>;
  }
  if (months <= 0) {
    return (
      <span className="inline-flex rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
        Current
      </span>
    );
  }
  if (months === 1) {
    return (
      <span className="inline-flex rounded-md bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
        1–2mo
      </span>
    );
  }
  return (
    <span className="inline-flex rounded-md bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700">
      2mo+
    </span>
  );
};

const WalletIcon = () => (
  <svg className="h-5 w-5 text-rose-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const UsersIcon = () => (
  <svg className="h-5 w-5 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const ClockIcon = () => (
  <svg className="h-5 w-5 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="h-5 w-5 text-violet-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const SummaryCard = ({ icon, iconBg, label, value, valueClassName = "text-stone-900" }) => (
  <div className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3.5 shadow-sm">
    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${iconBg}`}>
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-xs font-medium text-stone-500">{label}</p>
      <p className={`truncate text-lg font-bold tabular-nums leading-tight ${valueClassName}`}>
        {value}
      </p>
    </div>
  </div>
);

const TableHeader = () => (
  <thead className="border-b border-stone-100 bg-stone-50/50">
    <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500">
      <th className="px-4 py-2.5">Account</th>
      <th className="px-4 py-2.5">GL code</th>
      <th className="px-4 py-2.5 text-right">Latest credit</th>
      <th className="px-4 py-2.5">As of</th>
      <th className="px-4 py-2.5">Aging</th>
      <th className="px-4 py-2.5 text-right">Actions</th>
    </tr>
  </thead>
);

const ResolveNoteText = ({ note }) => {
  if (!note) return null;
  return (
    <p
      className="mt-1 max-w-[11rem] text-xs font-normal leading-snug text-stone-500 break-all line-clamp-2"
      title={note}
    >
      {note}
    </p>
  );
};

const InvoiceCreditLedgerModal = ({
  open,
  onClose,
  canMutate,
  onDownloadInvoice,
  downloadingId,
  onLedgerChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [active, setActive] = useState([]);
  const [resolved, setResolved] = useState([]);
  const [standingTotal, setStandingTotal] = useState(0);
  const [view, setView] = useState("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveNote, setResolveNote] = useState("");
  const [resolving, setResolving] = useState(false);
  const [reopeningKey, setReopeningKey] = useState(null);
  const [expandedProvider, setExpandedProvider] = useState(null);
  const [toast, setToast] = useState({
    message: "",
    type: "success",
    title: "",
  });

  const loadLedger = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getCreditLedger();
      const data = res?.data ?? {};
      setActive(Array.isArray(data.active) ? data.active : []);
      setResolved(Array.isArray(data.resolved) ? data.resolved : []);
      setStandingTotal(Number(data.standingTotal) || 0);
    } catch (err) {
      setError(err.message || "Failed to load credit ledger");
      setActive([]);
      setResolved([]);
      setStandingTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadLedger();
    setResolveTarget(null);
    setResolveNote("");
    setSearchQuery("");
    setExpandedProvider(null);
      // Clear any old toast
  setToast({
    title: "",
    message: "",
    type: "success",
  });
  }, [open, loadLedger]);

  const summary = useMemo(() => {
    const providers = new Set(active.map((e) => e.provider));
    let aging2moPlus = 0;
    for (const entry of active) {
      const months = monthsSinceAsOf(entry.as_of_month);
      if (months != null && months >= 2) aging2moPlus += 1;
    }
    return {
      providersAffected: providers.size,
      activeCount: active.length,
      aging2moPlus,
      resolvedCount: resolved.length,
    };
  }, [active, resolved]);

  const groupByProvider = useCallback((entries) => {
    const map = new Map();
    for (const entry of entries) {
      if (!matchesSearch(entry, searchQuery.trim())) continue;
      const list = map.get(entry.provider) ?? [];
      list.push(entry);
      map.set(entry.provider, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [searchQuery]);

  const activeByProvider = useMemo(() => groupByProvider(active), [active, groupByProvider]);
  const resolvedByProvider = useMemo(() => groupByProvider(resolved), [resolved, groupByProvider]);

  useEffect(() => {
    const list = view === "active"
      ? activeByProvider
      : resolvedByProvider;
  
    if (list.length > 0) {
      setExpandedProvider(list[0][0]); // First provider name
    } else {
      setExpandedProvider(null);
    }
  }, [view, activeByProvider, resolvedByProvider]);

  const toggleProvider = (provider) => {
    setExpandedProvider((current) =>
      current === provider ? null : provider
    );
  };

  if (!open) return null;

  const handleClose = () => {
    if (resolving || reopeningKey) return;
    setResolveTarget(null);
    setResolveNote("");

      // Clear any existing toast
  setToast({
    title: "",
    message: "",
    type: "success",
  });

    onClose();
  };

  const handleConfirmResolve = async () => {
    if (!resolveTarget || !resolveNote.trim()) return;
    setResolving(true);
    setError(null);
    try {
      await apiService.resolveCredit({
        provider: resolveTarget.provider,
        billingAccount: resolveTarget.billing_account,
        resolveNote: resolveNote.trim(),
        expectedVersion: resolveTarget.version ?? null,
      });
      setResolveTarget(null);
      setResolveNote("");
      await loadLedger();
      onLedgerChange?.();
    } catch (err) {
      if (err.status === 409) {
        // Someone else resolved/updated this credit in the meantime.
        // Refresh so the modal reflects the latest state rather than
        // letting the user resubmit against stale data.
        setError("This credit was updated by someone else. The ledger has been refreshed.");

setToast({
  title: "Changes Not Saved",
  message:
    "This credit was updated by another user while you were resolving it. \nThe latest data has been loaded. Please review and try again.",
  type: "warning",
});
        setResolveTarget(null);
        setResolveNote("");
        await loadLedger();
      } else if (err.status === 404) {
        setError("This credit no longer has an existing resolution to update.");

setToast({
  title: "Credit Not Found",
  message:
    "This credit was already resolved or removed by another user. The ledger has been refreshed.",
  type: "warning",
});
        setResolveTarget(null);
        setResolveNote("");
        await loadLedger();
      } else {
        setError(err.message || "Failed to resolve credit");
      }
    } finally {
      setResolving(false);
    }
  };

  const handleReopen = async (entry) => {
    const key = `${entry.provider}-${entry.billing_account}`;
    setReopeningKey(key);
    setError(null);
    try {
      await apiService.reopenCredit({
        provider: entry.provider,
        billingAccount: entry.billing_account,
        version: entry.version,   // <-- add this (required by the backend now)
      });
      await loadLedger();
      onLedgerChange?.();
    } catch (err) {
      if (err.status === 409) {
        setError("This credit was changed by someone else. The ledger has been refreshed.");

setToast({
  title: "Reopen Failed",
  message:
    "This credit was updated by another user and could not be reopened. \nThe latest data has been loaded. Please try again if needed.",
  type: "warning",
});
        await loadLedger();
      } else if (err.status === 404) {
        // Already reopened (or never resolved) by someone else — just
        // refresh so the row disappears from "Resolved" on its own.
        setError("This credit was already reopened elsewhere.");

setToast({
  title: "Already Reopened",
  message:
    "This credit was already reopened by another user. The ledger has been refreshed.",
  type: "info",
});
        await loadLedger();
      } else {
        setError(err.message || "Failed to reopen credit");
      }
    } finally {
      setReopeningKey(null);
    }
  };

  const renderActions = (entry, isResolved) => (
    <div className="flex items-center justify-end gap-1.5">
      {entry.invoice_id != null && (
        <button
          type="button"
          onClick={() => onDownloadInvoice?.(entry.invoice_id)}
          disabled={downloadingId === entry.invoice_id}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-stone-200 bg-white text-stone-500 hover:bg-stone-50 hover:text-stone-800 disabled:opacity-50"
          title="Download invoice"
        >
          <DownloadIcon className="w-4 h-4" />
        </button>
      )}
      {isResolved ? (
        canMutate && (
          <button
            type="button"
            onClick={() => handleReopen(entry)}
            disabled={reopeningKey === `${entry.provider}-${entry.billing_account}`}
            className="inline-flex items-center rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50"
          >
            {reopeningKey === `${entry.provider}-${entry.billing_account}` ? "Reopening…" : "Reopen"}
          </button>
        )
      ) : (
        canMutate && (
          <button
            type="button"
            onClick={() => {
              setResolveTarget(entry);
              setResolveNote("");
            }}
            className="inline-flex items-center gap-1 rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-50"
          >
            Resolve
          </button>
        )
      )}
    </div>
  );

  const renderDataRow = (entry, isResolved) => (
    <tr
      key={`${isResolved ? "resolved" : "active"}-${entry.provider}-${entry.billing_account}`}
      className="bg-white hover:bg-stone-50/40"
    >
      <td className="px-4 py-3 text-sm tabular-nums">
      <span className="font-medium text-stone-900">{entry.billing_account}</span>
      </td>
      <td className="px-4 py-3 text-sm text-stone-600">
        {entry.gl_code ? (
          <span className="font-mono text-xs text-stone-700">{entry.gl_code}</span>
        ) : (
          <span className="text-stone-400">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-right text-sm tabular-nums font-semibold text-stone-900">
        {formatCurrency(entry.latest_credit)}
      </td>
      <td className="px-4 py-3 align-top text-sm text-stone-600 max-w-[12rem]">
        <div className="whitespace-nowrap">{formatAsOf(entry.as_of_month)}</div>
        {isResolved && entry.resolve_note && (
          <ResolveNoteText note={entry.resolve_note} />
        )}
      </td>
      <td className="px-4 py-3">
        <AgingBadge asOfMonth={entry.as_of_month} />
      </td>
      <td className="px-4 py-3 text-right">
        {renderActions(entry, isResolved)}
      </td>
    </tr>
  );

  const renderProviderCard = (provider, entries, isResolvedSection) => {
    const groupKey = `${isResolvedSection ? "resolved:" : "active:"}${provider}`;

    const expanded = expandedProvider === provider;
    const groupTotal = entries.reduce((sum, e) => sum + (Number(e.latest_credit) || 0), 0);
    const accountLabel = `${entries.length} Account${entries.length === 1 ? "" : "s"}`;

    return (
      <div key={groupKey} className="bg-white">
        <button
          type="button"
          onClick={() => toggleProvider(provider)}
          className={`flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors ${
          expanded
    ? "bg-stone-50 hover:bg-stone-100"
    : "bg-white hover:bg-stone-50"
}`}
        >
          <span className="inline-flex items-center gap-2.5">
            <svg
              className={`h-4 w-4 shrink-0 text-stone-500 transition-transform ${expanded  ? "-rotate-90" : ""}`}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-bold text-stone-900">{provider}</span>
            <span className="inline-flex rounded-full bg-stone-200/70 px-2.5 py-0.5 text-xs font-medium text-stone-600">
              {accountLabel}
            </span>
          </span>
          {!isResolvedSection && (
            <span className="text-sm font-bold tabular-nums text-stone-900">
              {formatCurrency(groupTotal)}
            </span>
          )}
        </button>

        {expanded && (
          <div className="border-t border-stone-100 bg-white">
            <table className="min-w-full text-sm">
              <TableHeader />
              <tbody className="divide-y divide-stone-100">
                {entries.map((entry) => renderDataRow(entry, isResolvedSection))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const providersToShow =
  view === "active"
    ? activeByProvider
    : resolvedByProvider;

const hasVisibleData = providersToShow.length > 0;
  const isEmpty = active.length === 0 && resolved.length === 0;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-[#1c1c1a]/25 p-5 backdrop-blur-[10px]"
      style={{ WebkitBackdropFilter: "blur(10px)" }}
      role="presentation"
      onClick={handleClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="credit-ledger-title"
        className="relative z-[1] flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[12px] bg-white shadow-[0_24px_80px_rgba(28,28,26,0.28)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-[var(--line,#e4e2dc)] px-6 py-4">
          <div className="min-w-0">
            <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#a51c1c]">
              Credits
            </p>
            <h2 id="credit-ledger-title" className="mt-1 text-[22px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
              Credit ledger
            </h2>
            <p className="mt-1 text-[13px] text-[var(--muted,#6b6a66)]">
            Standing credits, one row per account showing the most recent month a credit appeared. A later month supersedes an earlier one, so a credit that changed shows its latest value.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={resolving}
            className="shrink-0 rounded-md p-1 text-[26px] leading-none text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)] hover:text-[var(--ink,#1c1c1a)] disabled:opacity-50"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Summary cards */}
        {!loading && !error && (
          <div className="grid grid-cols-1 gap-3 px-6 pb-4 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryCard
              icon={<WalletIcon />}
              iconBg="bg-rose-50"
              label="Standing credit total"
              value={formatCurrency(standingTotal)}
            />
            <SummaryCard
              icon={<UsersIcon />}
              iconBg="bg-emerald-50"
              label="Providers affected"
              value={`${summary.providersAffected} `}
            />
            <SummaryCard
              icon={<ClockIcon />}
              iconBg="bg-amber-50"
              label="Aging 2mo+"
              value={`${summary.aging2moPlus} `}
              valueClassName={summary.aging2moPlus > 0 ? "text-rose-600" : "text-stone-900"}
            />
            <SummaryCard
              icon={<CheckCircleIcon />}
              iconBg="bg-violet-50"
              label="Resolved "
              value={`${summary.resolvedCount} `}
            />
          </div>
        )}

        {/* Search + show resolved */}
        {!loading && !error && !isEmpty && (
          <div className="flex items-center gap-4 px-6 pb-4">
            <div className="relative flex-1">
              <svg
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search account or provider..."
                className="w-full rounded-lg border border-stone-200 py-2.5 pl-9 pr-3 text-sm text-stone-900 placeholder:text-stone-400 focus:border-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400"
              />
            </div>
            <div className="inline-flex rounded-lg border border-stone-200 bg-stone-100 p-1">
  <button
    type="button"
    onClick={() => setView("active")}
    className={`rounded-md px-4 py-2 text-sm font-medium transition-all ${
      view === "active"
        ? "bg-white text-stone-900 shadow-sm"
        : "text-stone-600 hover:text-stone-900"
    }`}
  >
    Active ({summary.activeCount})
  </button>

  <button
    type="button"
    onClick={() => setView("resolved")}
    className={`rounded-md px-4 py-2 text-sm font-medium transition-all ${
      view === "resolved"
        ? "bg-white text-stone-900 shadow-sm"
        : "text-stone-600 hover:text-stone-900"
    }`}
  >
    Resolved ({summary.resolvedCount})
  </button>
</div>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto px-6 pb-6">
          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-stone-200 border-t-stone-800" />
              <p className="mt-4 text-sm text-stone-500">Loading credit ledger…</p>
            </div>
          ) : isEmpty ? (
            <div className="rounded-xl border border-dashed border-stone-200 py-16 text-center">
              <p className="text-sm font-medium text-stone-600">No standing credits found.</p>
            </div>
          ) : !hasVisibleData ? (
            <div className="rounded-xl border border-dashed border-stone-200 py-12 text-center">
              {/* <p className="text-sm text-stone-500">No results match your search.</p> */}
              <p className="text-sm text-stone-500">
              {view === "resolved"
              ? "No resolved credits found."
              : "No active credits found."}
              </p>
            </div>
          ) : (
<div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
  {providersToShow.map(([provider, entries], index) => (
    <React.Fragment key={provider}>
      {/* {renderProviderCard(provider, entries, showResolved)} */}
      {renderProviderCard(provider, entries, view === "resolved")}
      {index !== providersToShow.length - 1 && (
        <div className="border-b border-stone-100" />
      )}
    </React.Fragment>
  ))}
</div>
          )}
        </div>
      </div>

      <Toast
  message={toast.message}
  type={toast.type}
  title={toast.title}
  onClose={() =>
    setToast({
      message: "",
      type: "success",
      title: "",
    })
  }
/>
      {resolveTarget && (
        <div
          className="fixed inset-0 z-[210] flex items-center justify-center bg-[#1c1c1a]/25 p-5 backdrop-blur-[10px]"
          style={{ WebkitBackdropFilter: "blur(10px)" }}
          role="presentation"
          onClick={() => {
            if (!resolving) {
              setResolveTarget(null);
              setResolveNote("");
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="resolve-credit-title"
            className="relative z-[1] w-full max-w-md overflow-hidden rounded-[12px] bg-white p-6 shadow-[0_24px_80px_rgba(28,28,26,0.28)]"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#a51c1c]">
              Credits
            </p>
            <h3 id="resolve-credit-title" className="mt-1 text-[20px] font-semibold text-[var(--ink,#1c1c1a)]">
              Resolve credit
            </h3>
            <p className="mt-1 text-sm text-stone-500 tabular-nums">
              {resolveTarget.provider} · {resolveTarget.billing_account} ·{" "}
              {formatCurrency(resolveTarget.latest_credit)}
            </p>
            <div className="mt-4">
              <label className="mb-1.5 block text-sm font-medium text-stone-700">
                Resolution note
              </label>
              <textarea
                value={resolveNote}
                onChange={(e) => setResolveNote(e.target.value)}
                rows={4}
                disabled={resolving}
                placeholder="Describe how this credit was resolved…"
                className={fieldClass}
              />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setResolveTarget(null);
                  setResolveNote("");
                }}
                disabled={resolving}
                className="inline-flex items-center justify-center rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmResolve}
                disabled={resolving || !resolveNote.trim()}
                className="inline-flex items-center justify-center rounded-md bg-[#a51c1c] px-4 py-2 text-[13px] font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {resolving ? "Resolving…" : "Confirm resolve"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceCreditLedgerModal;
